/**
 * Chat input section with the composer component and template fill dialog.
 */
import type { ReactNode } from 'react'

import type { ChatInputComposerProps } from '@/components/chat/chat-input-composer.interface'
import { ChatInputComposer } from '@/components/chat/chat-input-composer.organism'
import type { PromptTemplate } from '@/components/chat/prompt-template.interface'
import { TemplateFillDialog } from '@/components/chat/template-fill-dialog.organism'
import type { ParsedTemplateVariable } from '@/components/organisms/template-variable-editor/template-variable.interface'
import type { TemplateVariableFormValues } from '@/components/organisms/template-variable-form/template-variable-form.interface'

interface TemplateFillProps {
  selectedTemplate: PromptTemplate | null
  isTemplateFillOpen: boolean
  templateFillVariableList: ParsedTemplateVariable[]
  onTemplateFillSubmit: (values: TemplateVariableFormValues) => void
  onTemplateFillCancel: () => void
}

export type ChatInputSectionProps = ChatInputComposerProps & TemplateFillProps

const TemplateFillSection = ({
  selectedTemplate,
  isTemplateFillOpen,
  templateFillVariableList,
  onTemplateFillSubmit,
  onTemplateFillCancel,
}: TemplateFillProps): ReactNode => {
  if (!selectedTemplate) return null

  return (
    <TemplateFillDialog
      isOpen={isTemplateFillOpen}
      onOpenChange={(open) => {
        if (!open) onTemplateFillCancel()
      }}
      templateName={selectedTemplate.name}
      templateDescription={selectedTemplate.description}
      variableList={templateFillVariableList}
      onSubmit={onTemplateFillSubmit}
      onCancel={onTemplateFillCancel}
    />
  )
}

export const ChatInputSection = ({
  selectedTemplate,
  isTemplateFillOpen,
  templateFillVariableList,
  onTemplateFillSubmit,
  onTemplateFillCancel,
  ...composerProps
}: ChatInputSectionProps): ReactNode => (
  <div className="chat-input-area border-t bg-background p-4">
    <div className="chat-input-wrapper mx-auto w-full max-w-3xl md:pl-10 md:pr-12 lg:pl-12 lg:pr-14">
      <ChatInputComposer {...composerProps} />
    </div>
    <TemplateFillSection
      selectedTemplate={selectedTemplate}
      isTemplateFillOpen={isTemplateFillOpen}
      templateFillVariableList={templateFillVariableList}
      onTemplateFillSubmit={onTemplateFillSubmit}
      onTemplateFillCancel={onTemplateFillCancel}
    />
  </div>
)
